import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { IconAlertCircle, IconInfoCircle, IconPlus } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { DocumentsTable } from './DocumentsTable'
import { XwalkRules } from './XwalkRules'
import { actorLabel, useCpgFamilies, type CpgFamily, type CpgFamilyGroup } from './hooks'

function FamilyTree({
  groups,
  selectedId,
  onSelect,
}: {
  groups: CpgFamilyGroup[]
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  return (
    <nav className="p-2">
      {groups.map((group) => (
        <div key={group.id}>
          <div className="px-2 py-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
            {group.label}
          </div>
          {group.families.map((family) => (
            <button
              key={family.id}
              type="button"
              onClick={() => onSelect(family.id)}
              className={`flex w-full items-center gap-1 truncate rounded px-2 py-1 pl-4 text-left text-sm hover:bg-gray-100 ${
                selectedId === family.id ? 'bg-gray-200 font-medium' : ''
              } ${family.active ? '' : 'text-gray-400'}`}
            >
              <span className="truncate">{family.label}</span>
              <span className="ml-auto shrink-0 text-xs text-gray-400">
                {family.documentCount}d · {family.ruleCount}r
              </span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  )
}

function CreateFamilyModal({
  groups,
  onClose,
  onCreated,
}: {
  groups: CpgFamilyGroup[]
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [groupId, setGroupId] = useState<string | null>(null)
  const [slug, setSlug] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')

  const create = useMutation(
    orpc.createCpgFamily.mutationOptions({
      onSuccess: async ({ data }) => {
        await queryClient.invalidateQueries({ queryKey: orpc.listCpgFamilies.key() })
        onCreated(data.id)
        onClose()
      },
    }),
  )

  const slugOk = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)
  const ready = groupId !== null && slugOk && label.trim() !== '' && description.trim() !== ''

  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>New family</Text>} size="lg">
      <Stack gap="sm">
        <Select
          label="Group"
          data={groups.map((g) => ({ value: String(g.id), label: g.label }))}
          value={groupId}
          onChange={setGroupId}
        />
        <TextInput
          label="Slug"
          description="kebab-case, the classifier-facing identity — immutable once created"
          placeholder="chronic-kidney-disease"
          value={slug}
          onChange={(e) => setSlug(e.currentTarget.value)}
          error={slug !== '' && !slugOk ? 'kebab-case: a-z, 0-9, dashes' : undefined}
        />
        <TextInput
          label="Label"
          placeholder="Chronic Kidney Disease"
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
        />
        <Textarea
          label="Description"
          description="The shared contract read by value-set authors AND the classifier LLM — state inclusions, exclusions, boundaries"
          autosize
          minRows={4}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        {create.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {create.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={create.isPending}
            onClick={() =>
              create.mutate({
                groupId: Number(groupId),
                slug,
                label: label.trim(),
                description: description.trim(),
              })
            }
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function FamilyDetail({ family }: { family: CpgFamily }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [label, setLabel] = useState(family.label)
  const [description, setDescription] = useState(family.description)
  const [active, setActive] = useState(family.active)

  // Re-seed the editor when the selection changes (or a save comes back).
  useEffect(() => {
    setLabel(family.label)
    setDescription(family.description)
    setActive(family.active)
  }, [family])

  const update = useMutation(
    orpc.updateCpgFamily.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.listCpgFamilies.key() })
      },
    }),
  )

  const dirty =
    label !== family.label || description !== family.description || active !== family.active
  const deactivating = family.active && !active

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-gray-200 p-4">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs">
              <Badge variant="light" color="blue" radius="sm">
                {family.slug}
              </Badge>
              {!family.active && (
                <Badge variant="light" color="gray" radius="sm">
                  inactive
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed" className="mt-1">
              #{family.id} · updated {new Date(family.updatedAt).toLocaleString()} by{' '}
              {actorLabel(family.updatedBy)}
            </Text>
          </div>
          <Switch
            label="Active"
            checked={active}
            onChange={(e) => setActive(e.currentTarget.checked)}
          />
        </Group>
        <Stack gap="xs" className="mt-3">
          <TextInput
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
          />
          <Textarea
            label="Description"
            description="Drives the LLM classification of guidelines — edits apply to future classification runs only"
            autosize
            minRows={3}
            maxRows={10}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          {deactivating && family.documentCount > 0 && (
            <Alert color="orange" icon={<IconInfoCircle size={16} />}>
              {family.documentCount} document(s) are assigned to this family. Deactivating hides it
              from new assignments but keeps the existing ones.
            </Alert>
          )}
          {update.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {update.error.message}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              disabled={!dirty || label.trim() === '' || description.trim() === ''}
              loading={update.isPending}
              onClick={() =>
                update.mutate({
                  id: family.id,
                  ...(label !== family.label ? { label: label.trim() } : {}),
                  ...(description !== family.description
                    ? { description: description.trim() }
                    : {}),
                  ...(active !== family.active ? { active } : {}),
                })
              }
            >
              Save
            </Button>
          </Group>
        </Stack>
      </div>

      <Tabs defaultValue="rules" className="flex min-h-0 flex-1 flex-col" keepMounted={false}>
        <Tabs.List className="shrink-0">
          <Tabs.Tab value="rules">Matching rules ({family.ruleCount})</Tabs.Tab>
          <Tabs.Tab value="documents">Documents ({family.documentCount})</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="rules" className="min-h-0 flex-1">
          <XwalkRules familyId={family.id} />
        </Tabs.Panel>
        <Tabs.Panel value="documents" className="min-h-0 flex-1">
          <DocumentsTable
            scope="all"
            familyId={family.id}
            onSelect={(id) => navigate(`/super/cpg/documents?doc=${id}`)}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}

/**
 * Family registry management: groups → families tree (groups are seeded/read-only for
 * now), the family editor (label/description/active — the description is the classifier's
 * contract), per-family matching rules, and the documents assigned to the family.
 */
export function FamiliesView() {
  const familiesQuery = useCpgFamilies()
  const groups = useMemo(() => familiesQuery.data?.data ?? [], [familiesQuery.data])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const selected = useMemo(
    () => groups.flatMap((g) => g.families).find((f) => f.id === selectedId) ?? null,
    [groups, selectedId],
  )

  // Default to the first family once loaded.
  useEffect(() => {
    if (selectedId === null && groups.length > 0) {
      const first = groups.find((g) => g.families.length > 0)?.families[0]
      if (first) setSelectedId(first.id)
    }
  }, [groups, selectedId])

  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-72 shrink-0 flex-col border-r border-gray-200">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <FamilyTree groups={groups} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
        <div className="shrink-0 border-t border-gray-200 p-2">
          <Button
            fullWidth
            size="compact-sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => setCreating(true)}
          >
            New family
          </Button>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        {selected ? (
          <FamilyDetail family={selected} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {familiesQuery.isLoading ? 'Loading…' : 'Select a family.'}
          </div>
        )}
      </main>

      {creating && (
        <CreateFamilyModal
          groups={groups}
          onClose={() => setCreating(false)}
          onCreated={setSelectedId}
        />
      )}
    </div>
  )
}
